import type { ReactNode } from 'react';
import { hasPermission } from '@/auth/session';

/** True when the signed-in admin's role grants `permission`. The server re-checks every call. */
export function useCan(permission: string): boolean {
  return hasPermission(permission);
}

/** True when the role grants ANY of the permissions. */
export function canAny(...permissions: string[]): boolean {
  return permissions.some((p) => hasPermission(p));
}

/**
 * A page-level gate. Without the permission the page STATES what it needs instead of rendering a blank
 * screen or a wall of 403s — the same posture the explore and per-user pages already take.
 */
export function RequirePermission({
  permission, anyOf, what, children,
}: {
  permission?: string;
  anyOf?: string[];
  what: string;
  children: ReactNode;
}) {
  const needed = anyOf ?? (permission ? [permission] : []);
  const allowed = needed.length === 0 || needed.some((p) => hasPermission(p));
  if (allowed) return <>{children}</>;

  return (
    <div className="callout callout-warning" role="status" data-testid="permission-required">
      <span className="callout-icon" aria-hidden="true">!</span>
      <div className="t-body">
        <strong className="t-heading" style={{ display: 'block' }}>{what} is not available to your role</strong>
        It needs {needed.map((p, i) => (
          <span key={p}>
            {i > 0 && ' or '}
            <span className="mono">{p}</span>
          </span>
        ))}
        . Ask an owner if you should have it.
      </div>
    </div>
  );
}

/** Renders children only with the permission — for a write control inside a readable page. */
export function Can({ permission, children, fallback = null }: { permission: string; children: ReactNode; fallback?: ReactNode }) {
  return hasPermission(permission) ? <>{children}</> : <>{fallback}</>;
}
